// src/lib/learn/worked-example-choices.ts
//
// A multiple-choice worked example has to SHOW its choices.
//
// Review finding (Boundaries lesson): Worked Example 1 asked "Which choice
// completes the text…?" and showed no choices; its solution then walked
// through A–D and said "The answer is B." Example 3 was the same. The cause
// was the lesson generator: parseLessonResponse accepted an example's
// `choices` only when they were perfectly formed, and otherwise DROPPED them
// and kept the example — leaving a question about choices that don't exist.
// An audit of all 79 lessons (2026-10-06) found exactly those two; three
// older Punctuation examples carried their choices inline in the problem
// text ("**A)** `,`  **B)** `:` …") instead of as structured choices.
//
// This module is the one rule, shared by the generator (repair or drop), the
// lesson card (render inline choices as real choices), the admin review
// checklist (flag what's left), and the one-off content fix.
//
// Pure: the lesson card is a client component.

export type ChoiceLetter = 'A' | 'B' | 'C' | 'D'
export const CHOICE_LETTERS: readonly ChoiceLetter[] = ['A', 'B', 'C', 'D']

export type ExampleChoice = { label: ChoiceLetter; text: string }

export type ExampleWithChoices = {
  problem: string
  solution: string
  insight?: string
  choices?: ExampleChoice[]
  correctChoice?: ChoiceLetter
}

/** True when the example carries four A–D choices and a correct letter. */
export function hasStructuredChoices(
  ex: Pick<ExampleWithChoices, 'choices' | 'correctChoice'>,
): boolean {
  return (
    Array.isArray(ex.choices) &&
    ex.choices.length === 4 &&
    ex.choices.every((c, i) => c?.label === CHOICE_LETTERS[i] && !!c.text?.trim()) &&
    !!ex.correctChoice &&
    CHOICE_LETTERS.includes(ex.correctChoice)
  )
}

/**
 * Text that assumes lettered answer choices exist: "Which choice…",
 * "choice B", "the answer is B", "a comma (A)", "A) …". Letters must be
 * capitals, so math like "(a)" or "a = 2" doesn't count.
 */
export function refersToLetteredChoices(text: string): boolean {
  // No `i` flag where a letter is matched: "the answer is a fraction" must
  // not read as "the answer is A".
  return (
    /\b[Ww]hich choice\b/.test(text) ||
    /\b[Ww]hich of the following\b/.test(text) ||
    /\b(?:[Cc]hoice|[Oo]ption)s?\s+\(?\**[A-D]\b/.test(text) ||
    /\b[Aa]nswer\s+(?:is|:)\s*(?:[Cc]hoice\s+)?\(?\**[A-D]\b/.test(text) ||
    /(?:^|\s)\(([A-D])\)(?=[\s.,;:]|$)/.test(text) ||
    /(?:^|\n)\s*\**\(?[A-D][).]\**\s/.test(text)
  )
}

/**
 * The single letter a solution names as the answer ("**Answer: C**",
 * "The answer is **B**", "only **B** does"), or null when it names none or
 * more than one.
 */
export function answerLetterFromSolution(solution: string): ChoiceLetter | null {
  const found = new Set<ChoiceLetter>()
  // Capital letters only (no `i` flag): "the answer is a fraction" names no choice.
  const patterns = [
    /\b[Aa]nswer\s*(?:is|:)\s*(?:[Cc]hoice\s+)?\(?\**\(?([A-D])\b/g,
    /\b(?:[Cc]hoice|[Oo]ption)\s+\(?\**([A-D])\**\)?\s+is\s+(?:the\s+)?(?:correct|right|answer|best)\b/g,
    /\b[Oo]nly\s+\**\(?([A-D])\)?\**\s+(?:does|works|fits|is correct)\b/g,
  ]
  for (const p of patterns) {
    for (const m of solution.matchAll(p)) found.add(m[1] as ChoiceLetter)
  }
  return found.size === 1 ? [...found][0] : null
}

// A choice marker: "A)", "(A)", "A.", "A:", optionally bolded ("**A)**").
const MARKER = /(^|\s)(?:\*\*)?\(?([A-D])[).:](?:\*\*)?\s+/g

const unwrap = (t: string) =>
  t
    .trim()
    // `;` → ; — the card shows choice text as plain text.
    .replace(/^`([^`]+)`$/, '$1')
    .trim()

/**
 * Choices written inline in a problem ("**A)** `,`  **B)** `:` …" on one
 * line, or one per line), split out. Null unless A, B, C and D all appear, in
 * order, each with text. The stem keeps everything before A and anything
 * after D's paragraph.
 */
export function parseInlineChoices(
  problem: string,
): { stem: string; choices: ExampleChoice[] } | null {
  const marks: { letter: string; start: number; textStart: number }[] = []
  for (const m of problem.matchAll(MARKER)) {
    const start = (m.index ?? 0) + m[1].length
    marks.push({ letter: m[2], start, textStart: (m.index ?? 0) + m[0].length })
  }
  // The first run of A, B, C, D in order.
  for (let i = 0; i + 3 < marks.length; i++) {
    const run = marks.slice(i, i + 4)
    if (run.map((r) => r.letter).join('') !== 'ABCD') continue
    const dEnd = (() => {
      const para = problem.indexOf('\n\n', run[3].textStart)
      return para === -1 ? problem.length : para
    })()
    const choices: ExampleChoice[] = run.map((r, k) => ({
      label: CHOICE_LETTERS[k],
      text: unwrap(problem.slice(r.textStart, k < 3 ? run[k + 1].start : dEnd)),
    }))
    if (choices.some((c) => !c.text)) return null
    const stem = [problem.slice(0, run[0].start).trim(), problem.slice(dEnd).trim()]
      .filter(Boolean)
      .join('\n\n')
    if (!stem) return null
    return { stem, choices }
  }
  return null
}

/**
 * The example as it should be shown: unchanged when it already has
 * structured choices (or needs none); with inline choices moved into
 * `choices` when its problem lists them and its solution names one answer.
 */
export function normalizeWorkedExample<T extends ExampleWithChoices>(
  ex: T,
): T & Pick<ExampleWithChoices, 'choices' | 'correctChoice'> {
  if (hasStructuredChoices(ex)) return ex
  const inline = parseInlineChoices(ex.problem)
  if (!inline) return ex
  const letter = answerLetterFromSolution(ex.solution)
  if (!letter) return ex
  return { ...ex, problem: inline.stem, choices: inline.choices, correctChoice: letter }
}

/**
 * Why this example would show a question about choices that aren't there,
 * or null when it's fine (after normalizing inline choices).
 */
export function exampleChoiceIssue(ex: ExampleWithChoices): string | null {
  const shown = normalizeWorkedExample(ex)
  if (hasStructuredChoices(shown)) return null
  if (refersToLetteredChoices(shown.problem) || refersToLetteredChoices(shown.solution)) {
    return 'refers to answer choices A–D but shows none'
  }
  return null
}
